(() => {
  if (customElements.get("split-banner-component")) return;

  class SplitBanner extends HTMLElement {
    constructor() {
      super();
      this.index = 0;
      this.previousIndex = 0;
      this.timer = null;
      this.interval = 6000;
      this.autoplayEnabled = false;
      this.autoplayPaused = false;
      this.prefersReducedMotion = false;
      // Animation lock — matches CSS transition duration (1000ms).
      // Prevents rapid clicks from interrupting the slide transition.
      this.transitionDuration = 1000;
      this.isTransitioning = false;
      this.transitionTimer = null;
      this.reducedMotionQuery = null;
      this.inactiveDotWidth = "0.625rem";
      this.activeDotWidth = "2rem";
      this.leftPanels = [];
      this.rightPanels = [];
      this.copies = [];
      this.dots = [];
      this.prevButton = null;
      this.nextButton = null;
      this.sectionId = null;
      this.onPrevClick = this.onPrevClick.bind(this);
      this.onNextClick = this.onNextClick.bind(this);
      this.onDotClick = this.onDotClick.bind(this);
      this.onFocusIn = this.onFocusIn.bind(this);
      this.onFocusOut = this.onFocusOut.bind(this);
      this.onKeyDown = this.onKeyDown.bind(this);
      this.onBlockSelect = this.onBlockSelect.bind(this);
      this.onSectionSelect = this.onSectionSelect.bind(this);
      this.onReducedMotionChange = this.onReducedMotionChange.bind(this);
    }

    connectedCallback() {
      this.sectionId =
        this.closest(".shopify-section")?.dataset?.sectionId || null;
      this.leftPanels = Array.from(
        this.querySelectorAll("[data-split-banner-panel='left']"),
      );
      this.rightPanels = Array.from(
        this.querySelectorAll("[data-split-banner-panel='right']"),
      );
      this.copies = Array.from(this.querySelectorAll("[data-split-banner-copy]"));
      this.prevButton = this.querySelector("[data-split-banner-prev]");
      this.nextButton = this.querySelector("[data-split-banner-next]");
      this.dots = Array.from(this.querySelectorAll("[data-split-banner-dot]"));

      if (!this.copies.length) return;

      const autoplayAttr = this.dataset.sliderAutoplay;
      const intervalAttr = Number.parseInt(this.dataset.sliderInterval, 10);
      this.autoplayEnabled =
        autoplayAttr !== "false" && autoplayAttr !== "0" && autoplayAttr !== "";
      if (!Number.isNaN(intervalAttr) && intervalAttr > 0) {
        this.interval = intervalAttr;
      }
      this.reducedMotionQuery = window.matchMedia("(prefers-reduced-motion: reduce)");
      this.prefersReducedMotion = this.reducedMotionQuery.matches;

      if (this.prevButton) {
        this.prevButton.addEventListener("click", this.onPrevClick);
      }
      if (this.nextButton) {
        this.nextButton.addEventListener("click", this.onNextClick);
      }
      this.dots.forEach((dot) => dot.addEventListener("click", this.onDotClick));

      this.addEventListener("focusin", this.onFocusIn);
      this.addEventListener("focusout", this.onFocusOut);
      this.addEventListener("keydown", this.onKeyDown);

      if (window.Shopify && Shopify.designMode) {
        document.addEventListener("shopify:block:select", this.onBlockSelect);
        document.addEventListener("shopify:section:select", this.onSectionSelect);
      }
      if (this.reducedMotionQuery) {
        this.reducedMotionQuery.addEventListener("change", this.onReducedMotionChange);
      }

      this.updateSlides();
      this.startAutoplay();
    }

    disconnectedCallback() {
      if (this.prevButton) {
        this.prevButton.removeEventListener("click", this.onPrevClick);
      }
      if (this.nextButton) {
        this.nextButton.removeEventListener("click", this.onNextClick);
      }
      this.dots.forEach((dot) => dot.removeEventListener("click", this.onDotClick));
      this.removeEventListener("focusin", this.onFocusIn);
      this.removeEventListener("focusout", this.onFocusOut);
      this.removeEventListener("keydown", this.onKeyDown);
      if (window.Shopify && Shopify.designMode) {
        document.removeEventListener("shopify:block:select", this.onBlockSelect);
        document.removeEventListener("shopify:section:select", this.onSectionSelect);
      }
      if (this.reducedMotionQuery) {
        this.reducedMotionQuery.removeEventListener("change", this.onReducedMotionChange);
      }
      if (this.transitionTimer) {
        window.clearTimeout(this.transitionTimer);
        this.transitionTimer = null;
      }
      this.stopAutoplay();
    }

    get total() {
      return this.copies.length;
    }

    getPosition(index) {
      if (index === this.index) return "current";
      const diff = index - this.index;
      const length = this.total;
      const isNext = diff === 1 || diff === -(length - 1);
      return isNext ? "next" : "prev";
    }

    getLayer(index) {
      if (index === this.index) return "active";
      if (index === this.previousIndex) return "previous";
      return "other";
    }

    updateSlides() {
      const updatePanelSet = (panels) => {
        panels.forEach((panel, index) => {
          const position = this.getPosition(index);
          const layer = this.getLayer(index);
          panel.dataset.position = position;
          panel.dataset.layer = layer;
          panel.style.zIndex = layer === "active" ? "20" : layer === "previous" ? "10" : "0";
          panel.setAttribute("aria-hidden", index === this.index ? "false" : "true");
        });
      };

      updatePanelSet(this.leftPanels);
      updatePanelSet(this.rightPanels);

      this.copies.forEach((copy, index) => {
        const isActive = index === this.index;
        const position = this.getPosition(index);
        copy.dataset.position = position;
        copy.setAttribute("aria-hidden", isActive ? "false" : "true");
        copy.inert = !isActive;
      });

      this.dots.forEach((dot, index) => {
        const isActive = index === this.index;
        dot.dataset.active = isActive ? "true" : "false";
        dot.setAttribute("aria-current", isActive ? "true" : "false");
      });
      this.updateDotProgress();
    }

    goTo(index) {
      if (this.total < 2) return;
      // Block new transitions while one is in progress — prevents
      // broken animations from rapid clicks / keypresses.
      if (this.isTransitioning) return;
      let nextIndex = index;
      if (nextIndex < 0) nextIndex = this.total - 1;
      if (nextIndex >= this.total) nextIndex = 0;
      if (nextIndex === this.index) return;
      this.previousIndex = this.index;
      this.index = nextIndex;
      this.updateSlides();
      this.resetAutoplay();
      this.lockTransition();
    }

    lockTransition() {
      // Reduced motion disables CSS transition — no need to throttle.
      if (this.prefersReducedMotion) return;
      this.isTransitioning = true;
      if (this.transitionTimer) {
        window.clearTimeout(this.transitionTimer);
      }
      this.transitionTimer = window.setTimeout(() => {
        this.isTransitioning = false;
        this.transitionTimer = null;
      }, this.transitionDuration);
    }

    unlockTransition() {
      this.isTransitioning = false;
      if (this.transitionTimer) {
        window.clearTimeout(this.transitionTimer);
        this.transitionTimer = null;
      }
    }

    prev() {
      this.goTo(this.index - 1);
    }

    next() {
      this.goTo(this.index + 1);
    }

    onPrevClick() {
      this.prev();
    }

    onNextClick() {
      this.next();
    }

    onDotClick(event) {
      const dot = event.currentTarget;
      const index = Number.parseInt(dot.getAttribute("data-split-banner-dot"), 10);
      if (Number.isNaN(index)) return;
      this.goTo(index);
    }

    onFocusIn() {
      this.pauseAutoplay();
    }

    onFocusOut(event) {
      if (this.contains(event.relatedTarget)) return;
      this.resumeAutoplay();
    }

    onKeyDown(event) {
      if (
        event.target &&
        event.target.closest("input, textarea, select, [contenteditable]")
      ) {
        return;
      }
      if (event.key === "ArrowLeft") {
        event.preventDefault();
        this.prev();
      }
      if (event.key === "ArrowRight") {
        event.preventDefault();
        this.next();
      }
    }

    onBlockSelect(event) {
      const blockId = event?.detail?.blockId;
      const sectionId = event?.detail?.sectionId;
      if (this.sectionId && sectionId && this.sectionId !== sectionId) return;
      if (!blockId) return;
      const copy = this.querySelector(`[data-block-id="${blockId}"]`);
      if (!copy) return;
      const index = Number.parseInt(copy.dataset.index, 10);
      if (Number.isNaN(index)) return;
      this.goTo(index);
    }

    onSectionSelect(event) {
      const sectionId = event?.detail?.sectionId;
      if (this.sectionId && sectionId && this.sectionId !== sectionId) return;
      this.goTo(0);
    }

    onReducedMotionChange(event) {
      this.prefersReducedMotion = event.matches;
      if (this.prefersReducedMotion) {
        this.unlockTransition();
        this.stopAutoplay();
      } else if (this.autoplayEnabled) {
        this.startAutoplay();
      }
    }

    startAutoplay() {
      if (!this.autoplayEnabled || this.total < 2 || this.prefersReducedMotion) {
        this.setAttribute("aria-live", "polite");
        this.updateDotProgress();
        return;
      }
      this.stopAutoplay();
      this.autoplayPaused = false;
      this.setAttribute("aria-live", "off");
      this.startDotProgress();
      this.timer = window.setInterval(() => {
        if (this.autoplayPaused) return;
        this.previousIndex = this.index;
        this.index = (this.index + 1) % this.total;
        this.updateSlides();
        this.startDotProgress();
      }, this.interval);
    }

    stopAutoplay() {
      this.stopDotProgress();
      this.autoplayPaused = false;
      this.setAttribute("aria-live", "polite");
      if (!this.timer) return;
      window.clearInterval(this.timer);
      this.timer = null;
    }

    pauseAutoplay() {
      if (!this.autoplayEnabled || this.prefersReducedMotion) return;
      this.autoplayPaused = true;
      this.pauseDotProgress();
    }

    resumeAutoplay() {
      if (!this.autoplayEnabled || this.prefersReducedMotion) return;
      this.autoplayPaused = false;
      this.startAutoplay();
    }

    resetAutoplay() {
      if (!this.autoplayEnabled) return;
      this.startAutoplay();
    }

    updateDotProgress() {
      this.dots.forEach((dot, index) => {
        if (!dot) return;
        if (index !== this.index) {
          dot.style.setProperty("--split-banner-dot-duration", "0ms");
          dot.style.setProperty(
            "--split-banner-dot-fill-width",
            this.inactiveDotWidth,
          );
        }
      });
    }

    startDotProgress() {
      if (!this.autoplayEnabled) return;
      this.dots.forEach((dot, index) => {
        if (!dot) return;
        if (index !== this.index) {
          dot.style.setProperty("--split-banner-dot-duration", "0ms");
          dot.style.setProperty(
            "--split-banner-dot-fill-width",
            this.inactiveDotWidth,
          );
          return;
        }
        dot.style.setProperty("--split-banner-dot-duration", "0ms");
        dot.style.setProperty(
          "--split-banner-dot-fill-width",
          this.inactiveDotWidth,
        );
        void dot.offsetWidth;
        dot.style.setProperty(
          "--split-banner-dot-duration",
          `${this.interval}ms`,
        );
        dot.style.setProperty(
          "--split-banner-dot-fill-width",
          this.activeDotWidth,
        );
      });
    }

    stopDotProgress() {
      this.dots.forEach((dot) => {
        if (!dot) return;
        dot.style.setProperty("--split-banner-dot-duration", "0ms");
        dot.style.setProperty(
          "--split-banner-dot-fill-width",
          this.inactiveDotWidth,
        );
      });
    }

    pauseDotProgress() {
      const activeDot = this.dots[this.index];
      if (!activeDot) return;
      const width = this.getDotFillWidth(activeDot);
      activeDot.style.setProperty("--split-banner-dot-duration", "0ms");
      activeDot.style.setProperty("--split-banner-dot-fill-width", width);
    }

    getDotFillWidth(dot) {
      if (!dot) return this.inactiveDotWidth;
      const styles = window.getComputedStyle(dot, "::before");
      const width = styles.width;
      return width && width !== "auto" ? width : this.inactiveDotWidth;
    }
  }

  customElements.define("split-banner-component", SplitBanner);
})();
